import { AuthenticationStore, isStoredAccessTokenDueForRenewal, refreshAccessToken } from "@istari-frontend/auth";

import { resolveRegistryBaseUrl } from "~/shared/config/gateway-urls";

// Match the stream's token lifetime floor: the paused turn is still holding the
// MCP session, so the decision must carry a token with real life left.
const minTokenLifetimeSeconds = 10 * 60;

export type ApprovalAction = "accept" | "decline";

export interface ResolveApprovalParams {
  approvalId: string;
  action: ApprovalAction;
  signal?: AbortSignal;
}

/**
 * Deliver the user's decision for an `approvalRequest` the chat stream paused on.
 *
 * POSTs to `POST /api/v3/ai/chat/approvals/{approvalId}`. `accept` sends
 * `approve: true` (runs the gated call); `decline` is fail-closed (nothing runs).
 * The open chat stream resumes on its own once the server receives this — single
 * replica, so this must reach the instance holding the paused turn (the gateway
 * already routes a user's requests consistently).
 */
export async function resolveApproval({ approvalId, action, signal }: ResolveApprovalParams): Promise<void> {
  if (isStoredAccessTokenDueForRenewal(minTokenLifetimeSeconds)) {
    await refreshAccessToken(minTokenLifetimeSeconds);
  }
  const token = AuthenticationStore().getAccessToken() || "";
  const response = await fetch(
    `${resolveRegistryBaseUrl()}/api/v3/ai/chat/approvals/${encodeURIComponent(approvalId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ action, approve: action === "accept" }),
      signal,
    },
  );
  if (!response.ok) {
    throw Object.assign(new Error(`Approval resolve failed with status ${response.status}`), { response });
  }
}
